import { calculateTotal } from '@calculations/price/utilities/total';
import { SnapRoundingOptionsCustom } from '@calculations/round';
import { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import { Decimal } from '@snap-types/types/decimal';
import { WritableQuoteBuilderStateType } from '@ui/features/quote-builder/types/state';

import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../../utilities/action-guards';
import { OnBaseGrossMarginChangeInput } from '../types/base-gross-margin';
import { applyQuotePriceRecalcResult } from '../utilities/apply-quote-price-recalc-result';
import { buildStrategyCalculations } from '../utilities/build-strategy-calculations';

export function onPartBaseGrossMarginChange(
  state: WritableQuoteBuilderStateType,
  params: {
    quoteLineUuid: string;
    quotePriceUuid: string;
    baseGrossMargin: Decimal;
    suggestedPriceStatus?: SnapSuggestionStatus;
    priceStrategyConfigs: PriceStrategyConfigWithRelations[];
  },
) {
  const quoteLine = getQuoteLineOrThrow(state, params.quoteLineUuid);
  const quotePrice = getQuotePriceOrThrow(
    state,
    params.quoteLineUuid,
    params.quotePriceUuid,
  );
  // note: assume that any change without a specific param override
  // is a user-supplied value, indicating a rejection
  const suggestedPriceStatus =
    params.suggestedPriceStatus ?? SnapSuggestionStatus.REJECTED;
  const { priceStrategy } = quotePrice;

  const inputs = {
    state,
    quoteLine,
    quotePrice,
    baseGrossMargin: params.baseGrossMargin,
    priceStrategyConfigs: params.priceStrategyConfigs,
  } satisfies OnBaseGrossMarginChangeInput;

  const calculation = buildStrategyCalculations('onBaseGrossMarginChange')[
    priceStrategy
  ];

  if (!calculation) {
    console.error(
      'No target gross margin calculation function found for price strategy',
      priceStrategy,
    );
    return;
  }

  const output = calculation(inputs);

  if (output.status === 'failure') {
    console.error('Failed to calculate price', output.reason);
    return;
  }

  applyQuotePriceRecalcResult(state, {
    quoteLineUuid: params.quoteLineUuid,
    quotePriceUuid: params.quotePriceUuid,
    quotePrice,
    patch: {
      baseGrossMargin: params.baseGrossMargin,
      calculatedBaseGrossMargin: null,
    },
  });

  if (output.status === 'success') {
    const unitPrice = output.unitPrice;

    applyQuotePriceRecalcResult(state, {
      quoteLineUuid: params.quoteLineUuid,
      quotePriceUuid: params.quotePriceUuid,
      quotePrice,
      patch: {
        calculatedUnitPrice: unitPrice,
        priceAdjustmentAmount: output.priceAdjustmentAmount,
        unitPrice,
        baseUnitPrice: output.baseUnitPrice,
        userUnitPrice: null,
        grossMargin: output.grossMargin,
        suggestedPriceStatus,
        totalPrice: calculateTotal(
          unitPrice,
          quotePrice.quantity,
          SnapRoundingOptionsCustom(state),
        ),
      },
      rawFieldsToClear: ['unitPrice', 'priceAdjustmentAmount', 'grossMargin'],
    });
  }
  quoteLine.automatable = false;
  quoteLine.suggestedPricesStatus = suggestedPriceStatus;
}
